import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Car, CheckCircle2, ShieldCheck, Smartphone, AlertCircle, CalendarIcon } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";

const EMP = ["Salaried", "Self-Employed", "Business Owner", "Retired"];

/**
 * Public invite landing page. Accessed at /coborrower-invite/<token> or /guarantor-invite/<token>.
 * Token IS the auth — no login required.
 */
export default function InviteLanding({ kindHint }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0 = profile, 1 = aadhaar otp, 2 = consent, 3 = done
  const [form, setForm] = useState({
    full_name: "", relationship: "", mobile: "", email: "", pan: "", dob: "",
    monthly_income: 0, existing_emi: 0, employer_name: "", employment_type: "Salaried", address: "",
    cibil_consent: false, aa_consent: false,
  });
  const [aadhaar, setAadhaar] = useState("");
  const [otpRef, setOtpRef] = useState(null);
  const [otp, setOtp] = useState("");
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then(r => {
      setInvite(r.data);
      const pf = r.data.prefill || {};
      setForm(f => ({ ...f,
        full_name: pf.full_name || "",
        relationship: pf.relationship || "",
        mobile: pf.mobile || "",
        email: pf.email || "",
        pan: pf.pan || "",
        dob: pf.dob || "",
        monthly_income: pf.monthly_income || 0,
        existing_emi: pf.existing_emi || 0,
        employer_name: pf.employer_name || "",
        employment_type: pf.employment_type || "Salaried",
        address: pf.address || "",
      }));
      if (r.data.invite_status === "completed") setStep(3);
    }).catch(() => setInvite(false)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!invite) return (
    <div className="min-h-screen grid place-items-center p-4">
      <Card className="p-8 max-w-md text-center" data-testid="invite-invalid">
        <AlertCircle size={40} className="text-red-500 mx-auto mb-3" />
        <div className="text-lg font-semibold text-slate-900">Invite invalid or expired</div>
        <p className="text-sm text-slate-600 mt-2">This link could not be verified. Please check the URL or contact the person who invited you.</p>
        <Button className="mt-4 bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/")}>Go to homepage</Button>
      </Card>
    </div>
  );

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const kind = invite.kind; // "coborrower" or "guarantor"

  const requestOtp = async () => {
    if (!/^\d{12}$/.test(aadhaar)) { toast.error("Enter 12-digit Aadhaar"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/aadhaar-otp/request`, { aadhaar });
      setOtpRef(data.ref_id);
      toast.success(`OTP sent to Aadhaar-linked mobile. (Mock: use 123456)`);
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post(`/invites/${token}/aadhaar-otp/verify`, { ref_id: otpRef, otp });
      setAadhaarVerified(true);
      toast.success("Aadhaar verified");
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Wrong OTP"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.cibil_consent) { toast.error("CIBIL consent is required"); return; }
    setBusy(true);
    try {
      await api.post(`/invites/${token}/submit`, {
        ...form,
        monthly_income: Number(form.monthly_income),
        existing_emi: Number(form.existing_emi),
        aadhaar_otp_ref: aadhaarVerified ? otpRef : null,
      });
      setStep(3);
      toast.success("KYC submitted successfully");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(false); }
  };

  // Done state
  if (step === 3) return (
    <div className="min-h-screen gradient-navy text-white grid place-items-center p-4">
      <Card className="p-10 max-w-lg text-center bg-slate-800 border-slate-700 text-white" data-testid="invite-done">
        <CheckCircle2 size={56} className="text-teal-400 mx-auto mb-4" />
        <div className="text-2xl font-semibold">Thank you, {form.full_name || "applicant"}.</div>
        <p className="text-slate-300 mt-3">Your {kind} KYC for application <span className="font-mono text-teal-300">{invite.application_id}</span> has been submitted. Red Fort Capital Finance will reach out if any additional information is needed.</p>
        <div className="mt-6 text-xs uppercase tracking-wider text-slate-400">CarFin India · Red Fort Capital Finance NBFC</div>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="invite-landing">
      <header className="bg-slate-900 text-white border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/assets/carfin-logo.png" alt="CarFin India" className="h-9" />
          <div className="border-l border-slate-700 pl-3">
            <div className="text-[10px] uppercase tracking-wider text-teal-300">{kind === "coborrower" ? "Co-Borrower Invite" : "Guarantor Invite"}</div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Context card */}
        <Card className="p-5 border-teal-200 bg-teal-50 mb-6" data-testid="invite-context">
          <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2">You've been invited as a {kind}</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>{invite.context?.borrower_name}</strong> from <strong>{invite.context?.city || "India"}</strong> has applied for a CarFin used-car loan on a <strong>{invite.context?.vehicle} ({invite.context?.year})</strong>. They've requested you to support the application as their <strong>{kind}</strong>.
          </p>
          <div className="mt-3 text-sm grid grid-cols-2 md:grid-cols-3 gap-3">
            <KV label="Application" value={invite.application_id} />
            <KV label="Vehicle" value={invite.context?.vehicle} />
            <KV label="Loan amount" value={formatINR(invite.context?.loan_amount)} />
          </div>
        </Card>

        {/* Stepper */}
        <div className="flex items-center gap-3 mb-6 text-sm">
          {[
            { i: 0, t: "Your KYC" },
            { i: 1, t: "Aadhaar OTP" },
            { i: 2, t: "Consent & Submit" },
          ].map((s, idx, arr) => (
            <div key={s.i} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full grid place-items-center font-semibold ${step >= s.i ? "bg-teal-600 text-white" : "bg-slate-200 text-slate-500"}`}>{s.i + 1}</div>
              <div className={`${step === s.i ? "text-slate-900 font-medium" : "text-slate-500"}`}>{s.t}</div>
              {idx < arr.length - 1 && <div className="w-8 h-0.5 bg-slate-300 mx-1" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <Card className="p-6 border-slate-200" data-testid="invite-step-kyc">
            <div className="text-lg font-semibold text-slate-900 mb-1">Your details</div>
            <p className="text-sm text-slate-600 mb-4">Please confirm or fill the following.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F id="full_name" label="Full name (as per PAN)" form={form} set={set} />
              <F id="relationship" label="Relationship to borrower" form={form} set={set} />
              <F id="mobile" label="Mobile" form={form} set={set} />
              <F id="email" label="Email" form={form} set={set} />
              <F id="pan" label="PAN" form={form} set={set} />
              <DOBField value={form.dob} onChange={v => set("dob", v)} />
              <SelectF label="Employment type" options={EMP} value={form.employment_type} onChange={v => set("employment_type", v)} />
              <F id="employer_name" label="Employer / Business" form={form} set={set} />
              <F id="monthly_income" label="Monthly income (₹)" type="number" form={form} set={set} />
              <F id="existing_emi" label="Existing EMI (₹)" type="number" form={form} set={set} />
              <F id="address" label="Address" form={form} set={set} className="md:col-span-2" />
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={() => setStep(1)} disabled={!form.full_name || !form.pan} className="bg-slate-900 hover:bg-slate-800" data-testid="invite-next-kyc-btn">Continue</Button>
            </div>
          </Card>
        )}

        {step === 1 && (
          <Card className="p-6 border-slate-200" data-testid="invite-step-aadhaar">
            <div className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><Smartphone size={18} /> Aadhaar OTP verification</div>
            <p className="text-sm text-slate-600 mb-4">For demo: enter any 12-digit Aadhaar and use OTP <span className="font-mono font-semibold">123456</span>.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="aadhaar-input">Aadhaar (12 digits)</Label>
                <Input id="aadhaar-input" data-testid="invite-aadhaar-input" maxLength={12} value={aadhaar} onChange={e => setAadhaar(e.target.value.replace(/\D/g, ""))} />
              </div>
              <div className="flex items-end">
                <Button onClick={requestOtp} disabled={busy || !!otpRef} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="invite-aadhaar-request-otp-btn">{otpRef ? "OTP Sent" : "Send OTP"}</Button>
              </div>
              {otpRef && (
                <>
                  <div>
                    <Label htmlFor="otp-input">OTP (6 digits)</Label>
                    <Input id="otp-input" data-testid="invite-aadhaar-otp-input" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={verifyOtp} disabled={busy || otp.length !== 6} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="invite-aadhaar-verify-btn">Verify OTP</Button>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="invite-aadhaar-skip-btn">Skip for now</Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-6 border-slate-200" data-testid="invite-step-consent">
            <div className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><ShieldCheck size={18} /> Consent & Submit</div>
            <p className="text-sm text-slate-600 mb-4">Please review and provide consent.</p>
            <div className="space-y-3">
              <ConsentRow
                id="cibil"
                checked={form.cibil_consent}
                onChange={v => set("cibil_consent", v)}
                label="CIBIL credit bureau consent"
                description="I authorize Red Fort Capital Finance NBFC to obtain my credit information from CIBIL / Experian / Equifax / CRIF for the purpose of evaluating this loan application."
                required
              />
              <ConsentRow
                id="aa"
                checked={form.aa_consent}
                onChange={v => set("aa_consent", v)}
                label="Account Aggregator consent (optional)"
                description="I consent to share my bank statements via the RBI-regulated Account Aggregator framework to verify my income. This is voluntary but speeds up underwriting."
              />
              {aadhaarVerified && (
                <div className="flex items-center gap-2 text-sm text-teal-700 bg-teal-50 px-3 py-2 rounded-md border border-teal-200">
                  <CheckCircle2 size={16} /> Aadhaar OTP verified
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-4 p-3 bg-slate-50 rounded-md border border-slate-200">
              By submitting, you confirm that the information above is true and complete. Loan approval is subject to Red Fort Capital Finance credit policy, vehicle valuation, document verification, and final underwriting approval.
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={submit} disabled={busy || !form.cibil_consent} className="bg-teal-600 hover:bg-teal-700" data-testid="invite-submit-btn">{busy ? "Submitting…" : "Submit KYC"}</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function F({ id, label, form, set, type = "text", className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={`invite-${id}`}>{label}</Label>
      <Input id={`invite-${id}`} data-testid={`invite-${id}`} type={type} value={form[id] ?? ""} onChange={e => set(id, type === "number" ? Number(e.target.value) : e.target.value)} />
    </div>
  );
}

function DOBField({ value, onChange }) {
  // value is stored as ISO yyyy-mm-dd, displayed as dd/mm/yyyy (Indian locale)
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T00:00:00") : undefined;
  const display = selected
    ? selected.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
  const toISO = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const today = new Date();
  const eighteenAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return (
    <div>
      <Label htmlFor="invite-dob">Date of birth</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="invite-dob"
            type="button"
            variant="outline"
            data-testid="invite-dob"
            className="w-full justify-start text-left font-normal h-10"
          >
            <CalendarIcon size={16} className="mr-2 text-slate-500" />
            <span className={display ? "text-slate-900 font-mono" : "text-slate-400"}>
              {display || "dd/mm/yyyy"}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid="invite-dob-popover">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => { onChange(toISO(d)); setOpen(false); }}
            defaultMonth={selected || eighteenAgo}
            captionLayout="dropdown-buttons"
            fromYear={1940}
            toYear={today.getFullYear()}
            disabled={(d) => d > today}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
function SelectF({ label, options, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`invite-select-${label.toLowerCase().replace(/\s+/g, "-")}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-mono text-slate-900">{value || "—"}</div>
    </div>
  );
}
function ConsentRow({ id, checked, onChange, label, description, required }) {
  return (
    <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-md hover:border-teal-300 cursor-pointer" data-testid={`invite-consent-${id}`}>
      <Checkbox checked={checked} onCheckedChange={v => onChange(!!v)} className="mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-medium text-slate-900">{label} {required && <span className="text-red-600">*</span>}</div>
        <div className="text-xs text-slate-600 mt-1 leading-relaxed">{description}</div>
      </div>
    </label>
  );
}
